import axiosInstant from 'api/baseRequest'
import {
  EventApplicationUnitEnum,
  EventStatusEnum,
} from 'utils/constants/event'
import { REACT_APP_SERVER_API } from 'utils/constants/environment'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { useCallback, useEffect, useState } from 'react'

/** Katakana-only suffix (furiganaRegex: /^([ァ-ンヴ]|ー)+$/) — no ASCII digits */
const KATAKANA =
  'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン'

function katakanaSuffix(index: number): string {
  const a = KATAKANA[index % KATAKANA.length]
  const b = KATAKANA[(index * 17 + 3) % KATAKANA.length]
  return `${a}${b}`
}

function buildTestRegistrationPayload(options: {
  event: {
    _id: string
    questions?: Array<{ question: string; answer: string; order: number }>
    applicationUnit?: string
    remarksRequired?: boolean
  }
  timeslotId: string
  index: number
  pk: string | null
}) {
  const { event, timeslotId, index, pk } = options
  const suffix = katakanaSuffix(index)
  const guest =
    event.applicationUnit === EventApplicationUnitEnum.Multiple ? 1 : 1

  const questions =
    event.questions?.map((q) => ({
      ...q,
      tick: true,
    })) ?? []

  const unique = `${Date.now().toString(36)}${index.toString(36)}`
  const email = `evtbook-test-${unique}@testmail.com`

  /**
   * Phone: 10–11 digits, digits only (same rules as registration form)
   * 090 + 8 digits → 11 chars
   */
  const tail = String(10000000 + (index % 90000000))
    .padStart(8, '0')
    .slice(-8)
  const phoneNumber = `090${tail}`

  return {
    name: `負荷テスト${index + 1}`,
    furigana: `フカテスト${suffix}`,
    email,
    phoneNumber,
    guest,
    note: event.remarksRequired ? `備考テスト ${suffix}` : '',
    timeslotId,
    eventId: event._id,
    pk: pk ?? undefined,
    questions,
  }
}

/** POST /api/v1/reservation — id có thể nằm ở nhiều shape tùy backend */
function extractReservationId(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null
  const b = body as Record<string, unknown>
  const nested =
    b.data && typeof b.data === 'object'
      ? (b.data as Record<string, unknown>)
      : null
  const id =
    (typeof b._id === 'string' && b._id) ||
    (typeof b.id === 'string' && b.id) ||
    (typeof b.reservationId === 'string' && b.reservationId) ||
    (nested && typeof nested._id === 'string' && nested._id) ||
    (nested && typeof nested.id === 'string' && nested.id) ||
    (nested && typeof nested.reservationId === 'string' && nested.reservationId)
  if (typeof id === 'string' && id.trim()) return id.trim()
  return null
}

/** GET /api/v1/reservation/qr/:id — { success, data } hoặc data phẳng */
function extractQrData(body: unknown): {
  tokenId?: string
  shortCode?: string
  status?: string
  expiresAt?: string
} | null {
  if (!body || typeof body !== 'object') return null
  const b = body as Record<string, unknown>
  if (b.success === true && b.data && typeof b.data === 'object') {
    return b.data as {
      tokenId?: string
      shortCode?: string
      status?: string
      expiresAt?: string
    }
  }
  if (b.data && typeof b.data === 'object') {
    const d = b.data as Record<string, unknown>
    if (d.tokenId || d.shortCode)
      return d as { tokenId?: string; shortCode?: string }
  }
  if (typeof b.tokenId === 'string' || typeof b.shortCode === 'string') {
    return b as {
      tokenId?: string
      shortCode?: string
      status?: string
      expiresAt?: string
    }
  }
  return null
}

export type RegistrationQrRow = {
  index: number
  reservationId: string
  email: string
  timeslotId: string
  tokenId?: string
  shortCode?: string
  status?: string
  expiresAt?: string
  qrError?: string
  reservationIdMissing?: boolean
}

function buildRoundRobinQueue(
  timeslots: Array<{
    _id: string
    maxRegistrations: number
    registeredQuantity: number
  }>,
  maxTotal: number
): string[] {
  const slots = timeslots.map((s) => ({
    id: s._id,
    remaining: Math.max(0, s.maxRegistrations - s.registeredQuantity),
  }))

  const totalRemaining = slots.reduce((acc, s) => acc + s.remaining, 0)
  const target = Math.min(maxTotal, totalRemaining)

  const queue: string[] = []
  let count = 0

  while (count < target && slots.some((s) => s.remaining > 0)) {
    for (const s of slots) {
      if (s.remaining > 0 && count < target) {
        queue.push(s.id)
        s.remaining--
        count++
      }
    }
  }

  return queue
}

export default function GenerateRegistrationsTestPage() {
  const [eventId, setEventId] = useState('')
  const [pk, setPk] = useState('')
  const [maxCount, setMaxCount] = useState(20)
  const [loading, setLoading] = useState(false)
  const [event, setEvent] = useState<any>(null)
  const [logs, setLogs] = useState<string[]>([])
  const [summary, setSummary] = useState<{
    ok: number
    fail: number
  } | null>(null)
  const [qrRows, setQrRows] = useState<RegistrationQrRow[]>([])
  const [registrationIdsInput, setRegistrationIdsInput] = useState('')
  const [parsedIds, setParsedIds] = useState<string[]>([])
  const [parsingQrFromIds, setParsingQrFromIds] = useState(false)
  const [parsedIdQrRows, setParsedIdQrRows] = useState<RegistrationQrRow[]>([])

  useEffect(() => {
    document.title = 'Generate test registrations | Dev'
  }, [])

  const appendLog = useCallback((line: string) => {
    setLogs((prev) => [...prev, `[${new Date().toISOString()}] ${line}`])
  }, [])

  /** Tách ID từ textarea, gọi QR API và parse body bằng extractQrData */
  const parseRegistrationIdsAndExtractQr = useCallback(async () => {
    const ids = registrationIdsInput
      .split(/[,\s]+/)
      .map((id) => id.trim())
      .filter((id) => id.length > 0)
    setParsedIds(ids)
    setParsedIdQrRows([])

    if (!ids.length) {
      appendLog('No registration IDs to parse.')
      return
    }

    appendLog(`Parsed ${ids.length} registration ID(s), fetching QR…`)

    if (!REACT_APP_SERVER_API) {
      appendLog(
        'ERROR: REACT_APP_SERVER_API is not set. Configure env (see .env).'
      )
      return
    }

    setParsingQrFromIds(true)
    const collected: RegistrationQrRow[] = []

    try {
      for (let i = 0; i < ids.length; i++) {
        const reservationId = ids[i]
        const baseRow: RegistrationQrRow = {
          index: i + 1,
          reservationId,
          email: '',
          timeslotId: '',
        }

        try {
          const { data: qrBody } = await axiosInstant.request({
            url: `/api/v1/reservation/qr/${reservationId}`,
            method: 'get',
            headers: { 'Accept-Language': 'ja' },
          })
          const qr = extractQrData(qrBody)
          if (qr) {
            collected.push({
              ...baseRow,
              tokenId: qr.tokenId,
              shortCode: qr.shortCode,
              status: qr.status,
              expiresAt: qr.expiresAt,
            })
            appendLog(
              `  [${i + 1}/${ids.length}] ${reservationId} → shortCode=${qr.shortCode ?? '—'} tokenId=${qr.tokenId ? `${String(qr.tokenId).slice(0, 8)}…` : '—'}`
            )
          } else {
            collected.push({
              ...baseRow,
              qrError: 'Unexpected QR response shape',
            })
            appendLog(
              `  [${i + 1}/${ids.length}] ${reservationId} → QR: could not parse body`
            )
          }
        } catch (err: any) {
          const msg =
            err?.response?.data?.message ?? err?.message ?? String(err)
          collected.push({ ...baseRow, qrError: msg })
          appendLog(`  [${i + 1}/${ids.length}] ${reservationId} → ${msg}`)
        }
      }

      setParsedIdQrRows(collected)
      appendLog(`Done parsing QR for ${ids.length} id(s).`)
    } finally {
      setParsingQrFromIds(false)
    }
  }, [appendLog, registrationIdsInput])

  const fetchEvent = useCallback(async () => {
    if (!eventId.trim()) {
      appendLog('ERROR: Enter event ID.')
      return null
    }
    const { data } = await axiosInstant.request({
      headers: { 'Accept-Language': 'ja' },
      url: `/api/v1/event/public/${eventId.trim()}`,
      method: 'get',
    })
    return data
  }, [appendLog, eventId])

  const run = useCallback(async () => {
    setLoading(true)
    setLogs([])
    setSummary(null)
    setQrRows([])
    setEvent(null)

    try {
      if (!REACT_APP_SERVER_API) {
        appendLog(
          'ERROR: REACT_APP_SERVER_API is not set. Configure env (see .env).'
        )
        setLoading(false)
        return
      }

      const ev = await fetchEvent()
      if (!ev) {
        setLoading(false)
        return
      }

      setEvent(ev)

      if (ev.status !== EventStatusEnum.Open) {
        appendLog(
          `WARN: Event status is "${ev.status}" (expected open). Continuing anyway.`
        )
      }

      if (!ev.timeslots?.length) {
        appendLog('ERROR: Event has no timeslots.')
        setLoading(false)
        return
      }

      const pkVal = pk.trim() || null
      const queue = buildRoundRobinQueue(ev.timeslots, maxCount)

      appendLog(
        `Event: ${ev.title ?? ev._id} — scheduling ${queue.length} registration(s) (max ${maxCount}, round-robin across slots).`
      )

      let ok = 0
      let fail = 0
      const collected: RegistrationQrRow[] = []

      for (let i = 0; i < queue.length; i++) {
        const timeslotId = queue[i]
        const payload = buildTestRegistrationPayload({
          event: ev,
          timeslotId,
          index: i,
          pk: pkVal,
        })

        try {
          const { data: resBody } = await axiosInstant.request({
            url: '/api/v1/reservation',
            method: 'post',
            headers: { 'Accept-Language': 'ja' },
            data: {
              ...payload,
              guest: Number(payload.guest),
            },
          })
          ok++
          const reservationId = extractReservationId(resBody)
          if (!reservationId) {
            appendLog(
              `OK #${i + 1} slot=${timeslotId} email=${payload.email} — WARN: no reservation id in response (check body shape)`
            )
            collected.push({
              index: i + 1,
              reservationId: '',
              email: payload.email,
              timeslotId,
              reservationIdMissing: true,
              qrError: 'Missing reservation id from POST response',
            })
            continue
          }

          appendLog(
            `OK #${i + 1} reservationId=${reservationId} slot=${timeslotId} email=${payload.email}`
          )

          let qrRow: RegistrationQrRow = {
            index: i + 1,
            reservationId,
            email: payload.email,
            timeslotId,
          }

          try {
            const { data: qrBody } = await axiosInstant.request({
              url: `/api/v1/reservation/qr/${reservationId}`,
              method: 'get',
              headers: { 'Accept-Language': 'ja' },
            })
            const qr = extractQrData(qrBody)
            if (qr) {
              qrRow = {
                ...qrRow,
                tokenId: qr.tokenId,
                shortCode: qr.shortCode,
                status: qr.status,
                expiresAt: qr.expiresAt,
              }
              appendLog(
                `  QR shortCode=${qr.shortCode ?? '—'} tokenId=${qr.tokenId ? `${String(qr.tokenId).slice(0, 8)}…` : '—'}`
              )
            } else {
              qrRow.qrError = 'Unexpected QR response shape'
              appendLog(
                `  QR WARN: could not parse body (expected success + data)`
              )
            }
          } catch (qrErr: any) {
            const qm =
              qrErr?.response?.data?.message ?? qrErr?.message ?? String(qrErr)
            qrRow.qrError = qm
            appendLog(`  QR FAIL: ${qm}`)
          }

          collected.push(qrRow)
        } catch (err: any) {
          fail++
          const msg =
            err?.response?.data?.message ?? err?.message ?? String(err)
          appendLog(`FAIL #${i + 1} slot=${timeslotId} — ${msg}`)
        }
      }

      setQrRows(collected)
      setSummary({ ok, fail })
      appendLog(`Done. Success: ${ok}, Failed: ${fail}`)
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? String(err)
      appendLog(`ERROR: ${msg}`)
    } finally {
      setLoading(false)
    }
  }, [appendLog, fetchEvent, maxCount, pk])

  return (
    <div className="surface-ground min-h-screen">
      <div className="max-w-6xl mx-auto py-6 px-3">
        <h1 className="text-2xl font-semibold text-primary m-0 mb-2">
          Bulk test registrations
        </h1>
        <p className="text-sm text-600 mb-4 line-height-3">
          Fetches event via{' '}
          <code className="surface-100 border-round px-2 py-1 text-sm">
            GET /api/v1/event/public/:id
          </code>
          , then posts up to 20 (or your max) registrations with{' '}
          <code className="surface-100 border-round px-2 py-1 text-sm">
            POST /api/v1/reservation
          </code>{' '}
          (same shape as the registration screen). Slots are filled round-robin.
          After each successful registration, calls{' '}
          <code className="surface-100 border-round px-2 py-1 text-sm">
            GET /api/v1/reservation/qr/:reservationId
          </code>{' '}
          and lists reservation id, tokenId, and shortCode below. Emails use{' '}
          <code className="surface-100 border-round px-2 py-1 text-sm">
            @yopmail.com
          </code>
          . Uses{' '}
          <code className="surface-100 border-round px-2 py-1 text-sm">
            axiosInstant
          </code>{' '}
          (base URL from{' '}
          <code className="surface-100 border-round px-2 py-1 text-sm">
            REACT_APP_SERVER_API
          </code>
          ).
        </p>

        <div className="surface-card border-round border-1 surface-border p-4 shadow-1 flex flex-column gap-3">
          <label className="flex flex-column gap-2">
            <span className="font-semibold text-sm">Event ID *</span>
            <InputText
              className="w-full"
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              placeholder="e.g. 69f9b371ebfd9a9c7c645659"
            />
          </label>

          <label className="flex flex-column gap-2">
            <span className="font-semibold text-sm">pk (optional)</span>
            <InputText
              className="w-full"
              value={pk}
              onChange={(e) => setPk(e.target.value)}
              placeholder="If registration URL uses ?pk=..."
            />
          </label>

          <label className="flex flex-column gap-2">
            <span className="font-semibold text-sm">Max registrations</span>
            <InputText
              type="number"
              className="w-full"
              min={1}
              max={500}
              value={String(maxCount)}
              onChange={(e) => setMaxCount(Number(e.target.value) || 20)}
            />
          </label>

          <div className="border-top-1 surface-border pt-3 mt-2">
            <h2 className="text-lg font-semibold text-primary m-0 mb-3">
              Parse Registration IDs
            </h2>
            <label className="flex flex-column gap-2">
              <span className="font-semibold text-sm">
                Registration IDs (separated by space or comma)
              </span>
              <InputTextarea
                className="w-full"
                style={{ minHeight: '100px' }}
                autoResize
                value={registrationIdsInput}
                onChange={(e) => setRegistrationIdsInput(e.target.value)}
                placeholder="e.g. id1 id2, id3 id4,id5"
              />
            </label>
            <Button
              type="button"
              outlined
              disabled={parsingQrFromIds}
              loading={parsingQrFromIds}
              label="Parse IDs"
              className="mt-2"
              onClick={parseRegistrationIdsAndExtractQr}
            />
            {parsedIds.length > 0 && (
              <div className="mt-3 border-round p-3 bg-blue-50">
                <div className="font-semibold mb-2 text-sm">
                  Parsed IDs ({parsedIds.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {parsedIds.map((id, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 px-2 py-1 border-round text-xs font-mono"
                    >
                      {id}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {parsedIdQrRows.length > 0 && (
              <div className="mt-3">
                <div className="font-semibold mb-2 text-sm">
                  QR từ danh sách ID (extractQrData)
                </div>
                <div className="overflow-x-auto border-1 surface-border border-round">
                  <table className="w-full text-left text-sm">
                    <thead className="surface-100 font-semibold">
                      <tr>
                        <th className="px-2 py-2">#</th>
                        <th className="px-2 py-2">reservationId</th>
                        <th className="px-2 py-2">shortCode</th>
                        <th className="px-2 py-2">tokenId</th>
                        <th className="px-2 py-2">status</th>
                        <th className="px-2 py-2">expiresAt</th>
                        <th className="px-2 py-2">QR error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedIdQrRows.map((row) => (
                        <tr
                          key={`parsed-${row.index}-${row.reservationId}`}
                          className="border-top-1 surface-border align-top"
                        >
                          <td className="px-2 py-2 white-space-nowrap">
                            {row.index}
                          </td>
                          <td className="px-2 py-2 font-mono word-break-all max-w-14rem">
                            {row.reservationId}
                          </td>
                          <td className="px-2 py-2 font-mono white-space-nowrap">
                            {row.shortCode ?? '—'}
                          </td>
                          <td className="px-2 py-2 font-mono word-break-all max-w-20rem">
                            {row.tokenId ?? '—'}
                          </td>
                          <td className="px-2 py-2 white-space-nowrap">
                            {row.status ?? '—'}
                          </td>
                          <td className="px-2 py-2 font-mono text-xs word-break-all max-w-14rem">
                            {row.expiresAt ?? '—'}
                          </td>
                          <td className="px-2 py-2 text-red-500 word-break-all max-w-16rem">
                            {row.qrError ?? ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <Button
            type="button"
            disabled={loading || !eventId.trim()}
            loading={loading}
            label="Fetch event & submit registrations"
            onClick={run}
          />

          {event ? (
            <div className="border-round surface-50 p-3 text-sm">
              <div className="font-semibold mb-2">Loaded event</div>
              <div>Title: {event.title}</div>
              <div>Status: {event.status}</div>
              <div className="mt-2">
                Slots:{' '}
                {event.timeslots?.map((s: any) => (
                  <div key={s._id} className="ml-2 font-mono text-xs">
                    {s._id.slice(-6)} … remaining{' '}
                    {Math.max(0, s.maxRegistrations - s.registeredQuantity)} /{' '}
                    {s.maxRegistrations}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {summary ? (
            <div
              className={
                summary.fail > 0
                  ? 'text-orange-500 font-semibold'
                  : 'text-green-500 font-semibold'
              }
            >
              Result: {summary.ok} succeeded, {summary.fail} failed
            </div>
          ) : null}

          {qrRows.length ? (
            <div>
              <div className="font-semibold mb-2">
                Registration ↔ QR (tokenId / shortCode)
              </div>
              <div className="overflow-x-auto border-1 surface-border border-round">
                <table className="w-full text-left text-sm">
                  <thead className="surface-100 font-semibold">
                    <tr>
                      <th className="px-2 py-2">#</th>
                      <th className="px-2 py-2">reservationId</th>
                      <th className="px-2 py-2">shortCode</th>
                      <th className="px-2 py-2">tokenId</th>
                      <th className="px-2 py-2">status</th>
                      <th className="px-2 py-2">expiresAt</th>
                      <th className="px-2 py-2">email</th>
                      <th className="px-2 py-2">QR error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {qrRows.map((row) => (
                      <tr
                        key={`${row.index}-${row.reservationId || 'na'}`}
                        className="border-top-1 surface-border align-top"
                      >
                        <td className="px-2 py-2 white-space-nowrap">
                          {row.index}
                        </td>
                        <td className="px-2 py-2 font-mono word-break-all max-w-14rem">
                          {row.reservationIdMissing ? '—' : row.reservationId}
                        </td>
                        <td className="px-2 py-2 font-mono white-space-nowrap">
                          {row.shortCode ?? '—'}
                        </td>
                        <td className="px-2 py-2 font-mono word-break-all max-w-20rem">
                          {row.tokenId ?? '—'}
                        </td>
                        <td className="px-2 py-2 white-space-nowrap">
                          {row.status ?? '—'}
                        </td>
                        <td className="px-2 py-2 font-mono text-xs word-break-all max-w-14rem">
                          {row.expiresAt ?? '—'}
                        </td>
                        <td className="px-2 py-2 word-break-all max-w-16rem">
                          {row.email}
                        </td>
                        <td className="px-2 py-2 text-red-500 word-break-all max-w-16rem">
                          {row.qrError ?? ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-xs text-600">
                TSV (copy to spreadsheet):{' '}
                <span className="font-mono text-xs word-break-all">
                  reservationId{'\t'}shortCode{'\t'}tokenId
                </span>
              </div>
              <pre className="mt-1 max-h-8rem overflow-auto border-1 surface-border surface-50 border-round p-2 text-xs font-mono white-space-pre-wrap word-break-all">
                {qrRows
                  .filter((r) => r.reservationId && !r.reservationIdMissing)
                  .map(
                    (r) =>
                      `${r.reservationId}\t${r.shortCode ?? ''}\t${r.tokenId ?? ''}`
                  )
                  .join('\n')}
              </pre>
            </div>
          ) : null}

          {logs.length ? (
            <div>
              <div className="font-semibold mb-2">Log</div>
              <pre className="max-h-20rem overflow-auto border-1 surface-border bg-gray-900 border-round p-3 text-sm text-green-100 white-space-pre-wrap word-break-all">
                {logs.join('\n')}
              </pre>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
